import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Box,
  Breadcrumbs,
} from "@material-ui/core";
import _ from "lodash";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { Divider } from "./styles";
import TextView from "../../../../TextView";
import Grid from "@mui/material/Grid";
import { ASSETS } from "../../../../../utils/config";
import { colors } from "../../../../../utils";
import ReuseableButton from "../../../Button";
import SearchField from "../../ListComponents/SearchField";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  selectFolders,
  addFolders,
  addItemsToFolder,
  dialog,
  closeDialog,
} from "../../../../../redux/reducers/galleryPopup";
import { addItem, moveFolder } from "../../../../../redux/reducers/gallery";
import { getTitle, getFolderIcon } from "../Adopters";
import { toast } from "react-toastify";
import WheelComponent from "../../../../WheelComponent";
import { v4 as uuidv4 } from "uuid";
import BackdropSpinner from "../../ListComponents/BackdropSpinner";
import { useMoveFolder } from "../../../../../services/mutations";
import { API_PATH } from "../../../../../utils/config";
import API from "../../../../../services/request";
import { useParams } from "react-router-dom";
import {
  addParam,
  getLoggedInUser,
  mergeAndRemoveDuplicates,
} from "../../../../../utils/helpers";
import { ModalKey } from "../../../../../redux/ModalKey.enum";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  ".MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  ".MuiPaper-root": {
    border: "1px solid #E1E1E1",
    borderRadius: 8,
    width: "40vw",
    overflowY: "hidden",
  },
}));

const Move = () => {
  const { open, props } = useSelector(dialog(ModalKey.MOVE_FOLDER)) || {};
  const folders = useSelector(selectFolders);
  const [nestedFolders, setNestedFolders] = useState([]);
  const [folderHistory, setFolderHistory] = useState([]);
  const moveItem = useMoveFolder();
  const [selectedSort, setSelectedSort] = useState(null);
  const [destinationFolder, setDestinationFolder] = useState(null);
  const [sourceItem, setSourceItem] = useState(null);
  const [wheelData, setWheelData] = useState(null);
  const [search, setSearch] = useState("");
  const [parentId, setParentId] = useState(null);
  const { id } = useParams();
  const userId = getLoggedInUser()?.id;
  const [isLoading, setIsLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [rootPagination, setRootPagination] = useState({
    currentPage: 1,
    totalPages: 1,
  });

  const [nestedPagination, setNestedPagination] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const loadMoreRef = useRef(null);
  const dispatch = useDispatch();

  const getData = async (parentId, currentPage) => {
    if (isFetching) return;
    try {
      setIsLoading(true);
      setIsFetching(true);

      const query = addParam({
        user: id ? id : userId,
        parent: parentId,
        page: currentPage,
      });
      const response = await API.get(API_PATH.GET_FOLDERS + query);
      storeData(response);
    } catch (error) {
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  const storeData = (data) => {
    if (data?.data?.results?.length) {
      const groomedData = data?.data?.results.map((folder) => {
        const {
          id,
          name: title,
          parent: parentId,
          visibility,
          is_primary: isPrimary = false,
          updated_at: date,
          wheel_segments,
        } = folder;

        return !wheel_segments
          ? {
              id,
              title,
              items: [],
              parentId,
              visibility,
              isPrimary,
              date,
              type: "folder",
            }
          : folder;
      });

      if (parentId) {
        dispatch(
          addItemsToFolder({
            parentId: parentId,
            data: groomedData,
          })
        );

        setNestedPagination((prev) => ({
          ...prev,
          [parentId]: {
            currentPage: data?.data?.current_page,
            totalPages: data?.data?.total_pages,
          },
        }));
      } else {
        const mergedData = mergeAndRemoveDuplicates(folders, groomedData);
        dispatch(
          addFolders({
            folders: mergedData,
            folder_page: data?.data?.current_page,
            folder_total_pages: data?.data?.total_pages,
          })
        );

        setRootPagination({
          currentPage: data?.data?.current_page,
          totalPages: data?.data?.total_pages,
        });
      }
    }
  };

  useEffect(() => {
    open && getData(null, 1);
  }, [open]);

  useEffect(() => {
    if (props?.wheelData) setWheelData(props?.wheelData);
    if (props?.sourceFolder) setSourceItem(props?.sourceFolder);
  }, [props]);

  useEffect(() => {
    if (parentId) {
      setNestedPagination((prev) => ({
        ...prev,
        [parentId]: {
          currentPage: 1,
          totalPages: 1,
        },
      }));
      getData(parentId, 1);
    }
  }, [parentId]);

  const loadMoreData = (parentId = null) => {
    if (parentId) {
      const { currentPage, totalPages } = nestedPagination[parentId] || {};
      if (currentPage < totalPages) {
        getData(parentId, currentPage + 1);
      }
    } else {
      const { currentPage, totalPages } = rootPagination;
      if (currentPage < totalPages) {
        getData(null, currentPage + 1);
      }
    }
  };

  const loadMoreWheels = () => {
    if (folderHistory?.length > 0) {
      const currentFolderId = folderHistory[folderHistory.length - 1].id;
      loadMoreData(currentFolderId);
    } else {
      loadMoreData();
    }
  };

  const debouncedLoadMoreWheels = useCallback(
    _.debounce(loadMoreWheels, 1000),
    [loadMoreWheels]
  );

  const handleObserver = (entries) => {
    const target = entries[0];
    if (target.isIntersecting && !isFetching) {
      debouncedLoadMoreWheels();
    }
    if (target.isIntersecting) {
      console.log("KEEP CALLING");
    }
  };

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: "10px",
      threshold: 1.0,
    };

    const observer = new IntersectionObserver(handleObserver, option);

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [handleObserver]);

  useEffect(() => {
    if (folderHistory.length > 0) {
      const currentFolder = folderHistory[folderHistory.length - 1];
      const updatedNestedFolders = findNestedFolder(folders, currentFolder.id);
      setNestedFolders(updatedNestedFolders?.items || []);
    } else {
      setNestedFolders([]);
    }
  }, [folders, folderHistory]);

  const findNestedFolder = (folders, folderId) => {
    for (const folder of folders) {
      if (folder.id === folderId) {
        return folder;
      } else if (folder.items && folder.items.length > 0) {
        const nestedFolder = findNestedFolder(folder.items, folderId);
        if (nestedFolder) {
          return nestedFolder;
        }
      }
    }
    return null;
  };

  const reset = () => {
    setSelectedSort(null);
    setSearch("");
    setDestinationFolder(null);
    setSourceItem(null);
    setWheelData(null);
    setNestedFolders([]);
    setFolderHistory([]);
    setParentId(null);
    setRootPagination({
      currentPage: 1,
      totalPages: 1,
    });
    setNestedPagination({});
  };
  const handleCancel = () => {
    reset();
    dispatch(closeDialog({ key: ModalKey.MOVE_FOLDER }));
  };

  const hide = () => {
    reset();
    dispatch(closeDialog({ key: ModalKey.MOVE_FOLDER }));
  };

  function processText(htmlText) {
    let cleanedText = htmlText?.replace(/<\/?p>/gi, "");
    if (cleanedText?.length > 90)
      cleanedText = `${cleanedText?.slice(0, 90)}....`;
    return cleanedText || "";
  }

  const groomWheelSegment = (data) => {
    const wheelSegments = Array.isArray(data?.wheel_segments)
      ? data.wheel_segments
      : [];

    const wheelUpdatedData = [...wheelSegments]?.map((obj) => ({
      ...obj,
    }));

    const wheelUpdatedDataSorted = wheelUpdatedData.sort(
      (a, b) => a.position - b.position
    );
    return wheelUpdatedDataSorted;
  };

  const renderFolderListItem = (data) => {
    return data?.wheel_segments ? (
      <>
        <Grid
          container
          spacing={2}
          alignItems="center"
          sx={{ userSelect: "none" }}
        >
          <Grid item xs={2}>
            <WheelComponent
              userData={{
                id: uuidv4(),
                url: data?.wheel_image || ASSETS.userPlaceholder,
              }}
              maxSelectedArcs={0}
              arcThickness={-10}
              chartData={groomWheelSegment(data) || []}
              margin={25}
              setChartData={() => {}}
              containerDivStyle={{
                width: "80px",
                height: "80px",
              }}
            />
          </Grid>
          <Grid item xs={10}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={11.5}>
                <TextView variant="text">
                  {processText(data?.wheel_name) || ""}
                </TextView>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </>
    ) : (
      <Box
        key={data?.id}
        sx={{
          "&:hover": {
            backgroundColor: colors.PrimaryPurple100,
            borderRadius: 6,
          },
          backgroundColor:
            destinationFolder?.id === data?.id
              ? colors.PrimaryPurple100
              : "white",
          padding: 5,
          borderRadius: 1,
          userSelect: "none",
        }}
        onDoubleClick={() => {
          setParentId(data.id);
          setFolderHistory([...folderHistory, data]);
          setNestedFolders(data.items);
        }}
        onClick={() => {
          destinationFolder
            ? setDestinationFolder(null)
            : setDestinationFolder(data);
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid
            item
            xs={1}
            sx={{
              userSelect: "none",
            }}
          >
            <img src={getFolderIcon(data)} width={20} height={20} />
          </Grid>
          <Grid
            item
            xs={11}
            sx={{
              userSelect: "none",
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={11.5}>
                <TextView
                  variant="text"
                  style={{
                    paddingTop: "5px",
                  }}
                >
                  {getTitle(data?.title)}
                </TextView>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const handleMove = () => {
    const currentFolderId = folderHistory.length
      ? folderHistory[folderHistory.length - 1].id
      : null;
    const destinationId = destinationFolder?.id || currentFolderId;
    const payload = createPayload(wheelData, sourceItem, destinationId);
    setLoader(true);

    moveItem.mutate(payload, {
      onSuccess: () => handleSuccess(wheelData, sourceItem, destinationId),
      onError: () => {
        toast.error("Failed to move folder");
      },
      onSettled: () => setLoader(false),
    });
  };

  const createPayload = (wheelData, sourceItem, destinationId) => {};

  const handleSuccess = (wheelData, sourceItem, destinationId) => {
    if (wheelData) {
      dispatch(
        addItem({
          data: wheelData,
          parentId: destinationId,
        })
      );
    } else {
      dispatch(
        moveFolder({
          sourceId: sourceItem?.id,
          destinationId,
        })
      );
    }

    toast.success(`${wheelData ? "Wheel" : "Folder"} moved successfully`);
    hide();
  };

  const getListData = () => {
    let data = folderHistory.length > 0 ? nestedFolders : folders;

    if (search) {
      data = data?.filter((folder) =>
        getTitle(folder.title).toLowerCase().includes(search.toLowerCase())
      );
    }

    if (selectedSort) {
      data = data?.sort((a, b) => {
        if (selectedSort === "date") {
          return new Date(a.date) - new Date(b.date);
        } else if (selectedSort === "size") {
          return a.size - b.size;
        } else if (selectedSort === "content_amount") {
          return a.content_amount - b.content_amount;
        }
        return 0;
      });
    }

    return data?.filter((folder) => folder.id !== sourceItem?.id) || [];
  };

  const handleBreadcrumbClick = (index) => {
    const newHistory = folderHistory.slice(0, index + 1);
    setFolderHistory(newHistory);
    setNestedFolders(newHistory[newHistory.length - 1]?.items || []);
  };

  const handleBackClick = () => {
    if (folderHistory.length > 1) {
      const newHistory = folderHistory.slice(0, folderHistory.length - 1);
      setFolderHistory(newHistory);
      setNestedFolders(newHistory[newHistory.length - 1]?.items || []);
    } else {
      setFolderHistory([]);
      setNestedFolders(folders);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  const renderBreadcrumbs = () => (
    <Breadcrumbs aria-label="breadcrumb">
      {folderHistory.map((folder, index) => (
        <TextView
          key={folder.id}
          variant="subtitle"
          style={{ paddingTop: "1px", cursor: "pointer", userSelect: "none" }}
          onClick={() => handleBreadcrumbClick(index)}
        >
          {getTitle(folder.title)}
        </TextView>
      ))}
    </Breadcrumbs>
  );

  return (
    <StyledDialog
      open={open}
      onClose={hide}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <BackdropSpinner open={isLoading || loader} />
      <DialogTitle
        sx={{
          padding: "5px 10px",
        }}
      >
        <TextView variant="title" color={colors.PrimaryPurple900}>
          {`Move "${
            wheelData ? processText(wheelData.wheel_name) : sourceItem?.title
          }"`}
        </TextView>
      </DialogTitle>
      <Divider />

      <DialogContent>
        <Grid container spacing={2} sx={{ marginTop: 0.5 }}>
          <Grid item xs={12}>
            <SearchField
              value={search}
              setValue={handleSearch}
              height={40}
              onChange={(val) => handleSearch(val)}
            />
          </Grid>
        </Grid>

        {folderHistory.length > 0 && (
          <Grid container spacing={2} sx={{ marginY: 0.5 }}>
            <Grid item xs={0.9}>
              <ArrowBackIcon
                sx={{ cursor: "pointer", fontSize: 20 }}
                onClick={handleBackClick}
              />
            </Grid>
            <Grid item xs={11.1}>
              {renderBreadcrumbs()}
            </Grid>
          </Grid>
        )}

        <div
          style={{
            margin: "10px 0px",
            height: "40vh",
            minHeight: "10vh",
            overflowY: "auto",
          }}
        >
          {getListData()?.map((folder) => renderFolderListItem(folder))}
        </div>

        <Divider />
        <div
          id="load-more-anchor"
          ref={loadMoreRef}
          style={{
            height: "20px",
            visibility: isLoading ? "hidden" : "visible",
          }}
        />
        <DialogActions>
          <div style={{ marginTop: 10 }}>
            <ReuseableButton
              onClick={handleCancel}
              text={"Cancel"}
              fullWidth={false}
              variant="text"
              textColor="black"
              customStyles={{
                padding: "4px 20px",
              }}
            />

            <ReuseableButton
              text={"Move"}
              fullWidth={false}
              customStyles={{
                backgroundColor: colors.PrimaryPurple,
                padding: "4px 20px",
              }}
              onClick={handleMove}
            />
          </div>
        </DialogActions>
      </DialogContent>
    </StyledDialog>
  );
};

export default Move;
